// @flow
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import PetTypeService from '../../../../../services/petTypeService';
import useIntl from '../../../../../hooks/useIntl';
import type { PetTypeApiRecord, PetTypeOption } from '../../types';

// Keep this empty to prevent component-library validation on mount.
export const DEFAULT_OPTION_VALUE = '';

/** Builds localized pet-type options with embedded breed options. */
export const buildPetTypeOptions = ({
  petTypes,
  locale,
  chooseLabel,
}: {
  petTypes: ?Array<PetTypeApiRecord>,
  locale: string,
  chooseLabel: string,
}): Array<PetTypeOption> => {
  const defaultBreedOption = {
    value: DEFAULT_OPTION_VALUE,
    text: chooseLabel,
    disabled: true,
  };

  return [
    {
      value: DEFAULT_OPTION_VALUE,
      text: chooseLabel,
      disabled: true,
      petBreeds: [defaultBreedOption],
    },
    ...(petTypes ?? []).map((petType) => ({
      value: petType.id,
      // Use the source name when a translation is unavailable.
      text: petType.translations?.[locale] || petType.name || '',
      disabled: false,
      requiresWeight: Boolean(petType.requiresWeight),
      petBreeds: [
        defaultBreedOption,
        ...(petType.petBreeds ?? []).map((breed) => ({
          value: breed.id,
          text: breed.translations?.[locale] || breed.name || '',
          disabled: false,
        })),
      ],
    })),
  ];
};

const usePetTypeOptionsQuery = ({
  chooseLabel,
}: {
  chooseLabel: string,
}): {
  petTypeOptions: Array<PetTypeOption>,
  isLoading: boolean,
  isError: boolean,
} => {
  const intl = useIntl();
  const petTypeService = useMemo(() => new PetTypeService(), []);

  const { data, isLoading, isError } = useQuery(
    ['petTypeOptions'],
    () => petTypeService.getAll(),
    // Pet types and breeds are static reference data for this session.
    { staleTime: Infinity },
  );

  const locale = intl?.locale ?? 'en';

  const petTypeOptions = useMemo(
    () => buildPetTypeOptions({ petTypes: data, locale, chooseLabel }),
    [data, locale, chooseLabel],
  );

  return { petTypeOptions, isLoading, isError };
};

export default usePetTypeOptionsQuery;
